import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import MenuItemCard from "@/components/MenuItemCard";
import { useCart } from "@/context/CartContext";
import { ShoppingBag } from "lucide-react";
import { money } from "@/lib/api";

export default function MenuPage() {
  const [categories, setCategories] = useState([]);
  const [menu, setMenu] = useState([]);
  const [active, setActive] = useState("all");
  const [paused, setPaused] = useState(false);
  const [openId, setOpenId] = useState(null);
  const { count, subtotal, setOpen } = useCart();
  const sectionRefs = useRef({});
  const [searchParams] = useSearchParams();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setPaused(r.data.ordering_paused)).catch(() => {});
  }, []);

  const openName = searchParams.get("open");
  useEffect(() => {
    if (!openName || menu.length === 0) return;
    const target = menu.find((m) => m.name.toLowerCase() === openName.toLowerCase());
    if (!target) return;
    setActive("all");
    setOpenId(target.id);
    setTimeout(() => {
      const el = document.getElementById(`item-${target.id}`);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 150, behavior: "smooth" });
    }, 350);
  }, [openName, menu]);

  const grouped = useMemo(() => {
    const g = {};
    categories.forEach((c) => (g[c.id] = menu.filter((m) => m.category === c.id)));
    return g;
  }, [categories, menu]);

  const scrollTo = (id) => {
    setActive(id);
    const el = sectionRefs.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 130, behavior: "smooth" });
  };

  const visibleCats = active === "all" ? categories : categories.filter((c) => c.id === active);

  return (
    <div className="pb-28">
      {/* Category subnav */}
      <div className="sticky top-16 z-30 border-b border-stone-200 bg-[#FAF6F0]/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl gap-2 overflow-x-auto px-4 py-3 no-scrollbar sm:px-6">
          <button
            data-testid="category-tab-all"
            onClick={() => setActive("all")}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
              active === "all" ? "bg-orange-700 text-white" : "bg-white text-stone-700 hover:bg-stone-100"
            }`}
          >
            All Items
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`category-tab-${c.id}`}
              onClick={() => scrollTo(c.id)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                active === c.id ? "bg-orange-700 text-white" : "bg-white text-stone-700 hover:bg-stone-100"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {paused && (
        <div className="mx-auto mt-6 max-w-6xl px-6">
          <div className="rounded-2xl border border-amber-400 bg-amber-100 px-5 py-4 text-amber-900" data-testid="ordering-paused-banner">
            <strong>Online ordering is currently paused.</strong> You can browse the menu, but checkout is temporarily unavailable.
          </div>
        </div>
      )}

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <h1 className="font-display text-4xl font-extrabold text-stone-900">Menu</h1>
        <p className="mt-2 text-stone-600">Order Nigerian favorites for pickup in Orlando.</p>

        {visibleCats.map((c) => (
          <section key={c.id} ref={(el) => (sectionRefs.current[c.id] = el)} className="mt-10">
            <h2 className="font-display text-2xl font-bold text-stone-900">{c.name}</h2>
            <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {(grouped[c.id] || []).map((item) => (
                <MenuItemCard key={item.id} item={item} autoOpen={openId === item.id} />
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Mobile sticky order bar */}
      {count > 0 && (
        <button
          data-testid="mobile-order-bar"
          onClick={() => setOpen(true)}
          className="fixed inset-x-4 bottom-4 z-30 flex items-center justify-between rounded-full bg-orange-700 px-6 py-4 text-white shadow-xl transition-colors duration-200 hover:bg-orange-800 sm:hidden"
        >
          <span className="flex items-center gap-2 font-bold"><ShoppingBag className="h-5 w-5" /> {count} item{count > 1 ? "s" : ""}</span>
          <span className="font-bold">{money(subtotal)}</span>
        </button>
      )}
    </div>
  );
}
